import { useAuthStore } from 'context/AuthStore'
import { CreditFlow } from 'lib/api/services/creditFlow.service'
import { initialNewLoanApplication, initLoanApplicationData } from 'lib/constants/initial-states'
import { MANAGER } from 'lib/constants/roles'
import { useResErrors } from 'lib/hooks'
import { checkCreditProduct, getFullName, parseJson } from 'lib/utils'
import { enqueueSnackbar } from 'notistack'
import { createContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router'

export const ContractStoreContext = createContext({
  contractStore: {},
  contractStoreFunc: {}
})

const getStatusList = data => {
  const isCLProduct = checkCreditProduct('CL', data?.creditProduct?.name)

  const clSections = [
    {
      name: 'Рассмотрение заявки',
      codes: [
        'userUnderwriterDecision',
        'serviceWingsData',
        'serviceRefuse',
        'serviceInvalid',
        'userAlternative',
        'userWingsData',
        'userRevert',
        'userClientConsidering'
      ]
    },
    { name: 'Согласие клиента', codes: ['userClientAgreement'] },
    { name: 'Подписание клиентом', codes: ['userUploadClientBankDocs'] },
    { name: 'Подписание МФО', codes: ['userUploadMfoBankDocs'] },
    { name: 'Финальная проверка', codes: ['userCheckBankDocs'] },
    { name: 'Кредит выдан', codes: ['serviceComplete'] }
  ]

  const alSections = [
    {
      name: 'Рассмотрение заявки',
      codes: [
        'userUnderwriterDecision',
        'serviceWingsData',
        'serviceRefuse',
        'serviceInvalid',
        'userAlternative',
        'userWingsData',
        'userRevert',
        'userClientConsidering'
      ]
    },
    { name: 'Принятие условий', codes: ['userClientAgreement'] },
    { name: 'Документы дилера', codes: ['userUploadCollateralDocs'] },
    { name: 'Проверка залога', codes: ['userCheckCollateral'] },
    { name: 'Подписание клиентом', codes: ['userUploadClientBankDocs'] },
    { name: 'Подписание МФО', codes: ['userUploadMfoBankDocs'] },
    { name: 'Финальная проверка', codes: ['userCheckBankDocs'] },
    { name: 'Кредит выдан', codes: ['serviceComplete'] }
  ]

  return isCLProduct ? clSections : alSections
}

const ContractStoreProvider = props => {
  const { standardCheck } = useResErrors()
  const { authStore, authStoreFunc } = useAuthStore()
  const location = useLocation()

  const [step, setStep] = useState(0)
  const [stepCode, setStepCode] = useState('userUnderwriterDecision')
  const [loanApplicationData, setLoanApplicationData] = useState(initLoanApplicationData)
  const [newClient, setNewClient] = useState(initLoanApplicationData.client)
  const [newCollaterals, setNewCollaterals] = useState(initLoanApplicationData.collaterals)
  const [newLoanApplication, setNewLoanApplication] = useState(initialNewLoanApplication)
  const [client, setClient] = useState(initLoanApplicationData.client)
  const [collateral, setCollateral] = useState(initLoanApplicationData.collaterals)
  const [errorFields, setErrorFields] = useState({})
  const [creditProducts, setCreditProducts] = useState([])
  const [managerWorking, setManagerWorking] = useState(false)
  const [chatHistory, setChatHistory] = useState([])
  const [fileCategories, setFileCategories] = useState([])
  const [banks, setBanks] = useState([])
  const [bsData, setBsData] = useState(null)
  const [alternatives, setAlternatives] = useState(null)
  const [dealerCenters, setDealerCenters] = useState([])
  const [dealerCenter, setDealerCenter] = useState([])
  const [carBrands, setCarBrands] = useState([])
  const [carModels, setCarModels] = useState([])
  const [printForms, setPrintForms] = useState([])
  const [dds, setDds] = useState([])

  const clientFileTypes = [
    'INITIAL_PAYMENT_LETTER',
    'PURCHASE_SALE_AGREEMENT',
    'DATA_PROCESSING_CONSENT',
    'CLIENT_IDENTITY_DOCUMENT',
    'SIGNED_QUESTIONNAIRE',
    'PAYMENT_INVOICE',
    'INCOME_PROOF_DOCUMENTS',
    'CLIENT_PHOTO'
  ]

  const collateralFileTypes = [
    'PURCHASE_SALE_AGREEMENT',
    'VEHICLE_REGISTRATION_CERTIFICATE',
    'CONSENT',
    'VEHICLE_PLEDGE_AGREEMENT',
    'DOWNPAYMENT_RECEIPT',
    'PAYMENT_INVOICE_COLLATERAL_DOCS',
    'EXTRAJUDICIAL_CONSENT',
    'IS_REG_DOC',
    'PHOTO_COLLATERAL',
    'PHOTO_DMK',
    'BANK_STATMENT',
    'ADDITINAL',
    'REPORT_OF_MOVABLE_PROPERTY',
    'CERTIFICATE_OF_STATE_REGISTRATION_OF_PLEDGE_OF_MOVABLE_PROPERTY',
    'UTIL'
  ]
  const [AgreementFile, setAgreementFile] = useState('')
  const [SingleFile, setSingleFile] = useState('')
  const [GaranteeFile, setGaranteeFile] = useState('')
  const [ClientDocs, setClientDocs] = useState([])

  const stepperList = getStatusList(loanApplicationData)

  const findCurrentStepIndex = code => {
    return stepperList.findIndex(s => s.codes.includes(code))
  }

  useEffect(() => {
    const statusCode = loanApplicationData?.status?.code
    if (!statusCode) return

    const currentStepIndex = findCurrentStepIndex(statusCode)

    if (currentStepIndex !== -1) {
      setStep(currentStepIndex)
      setStepCode(statusCode)
    }
  }, [loanApplicationData?.status?.code])

  const getApplicationById = async id => {
    try {
      const response = await CreditFlow.getApplicationById(id)
      standardCheck(response, () => {
        if (response.data) {
          setLoanApplicationData(response.data)
          if (location.pathname.includes('new-application') || response.data?.status?.code === 'userRevert') {
            setNewClient(response.data.client)
            setNewLoanApplication({
              id: response.data.id,
              creditProduct: response.data.creditProduct,
              loanAmount: response.data.loanAmount,
              currency: response.data.currency,
              disbursementMethod: 'Банковский перевод',
              loanPurpose: response.data.loanPurpose,
              paymentIntervalDays: response.data.paymentIntervalDays,
              numberOfTranches: response.data.numberOfTranches,
              interestRate: response.data.interestRate,
              loanType: response.data.loanType,
              loanStage: response.data.loanStage
            })
            if (newCollaterals.length === 0) {
              setNewCollaterals(response.data.collaterals)
            }

            setClientDocs(response.data.collateralFiles || [])
          }
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getChatHistory = async id => {
    try {
      const response = await CreditFlow.getChatHistory(id)
      standardCheck(response, () => {
        if (response.data) {
          const parsedData = response?.data?.map(item => parseJson(item))
          setChatHistory(parsedData)
        }
      })
    } catch (error) {
      console.error('Ошибка получения истории чата:', error)
    }
  }

  const getFileCategories = async id => {
    try {
      const response = await CreditFlow.getFileCategories(id)
      standardCheck(response, () => {
        if (response.data) {
          setFileCategories(response.data?.list)
        }
      })
    } catch (error) {
      console.error('Ошибка получения истории чата:', error)
    }
  }

  const getBanks = async () => {
    try {
      const response = await CreditFlow.getBanks()
      if (response.data) {
        setBanks(response.data?.list)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getBsData = async () => {
    try {
      const response = await CreditFlow.getBsData()
      if (response.data) {
        setBsData(response.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getAlternatives = async id => {
    try {
      const response = await CreditFlow.getAlternatives(id)
      if (response.data) {
        setAlternatives(response.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const addComment = async (id, text) => {
    try {
      const data = {
        loanApplicationId: id,
        text
      }
      const response = await CreditFlow.addComment(data)
      standardCheck(response, () => {
        if (response?.status === 200) {
          getChatHistory(id)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getClientById = async id => {
    try {
      const response = await CreditFlow.getClientById(id)
      standardCheck(response, () => {
        if (response.data) {
          setClient(response.data)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getCollateralById = async id => {
    try {
      const response = await CreditFlow.getCollateralById(id)
      standardCheck(response, () => {
        if (response.data) {
          setCollateral(response.data)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getDDS = async id => {
    try {
      const response = await CreditFlow.getDDS(id)
      standardCheck(response, () => {
        if (response.data) {
          setDds(response.data)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getDealerCenters = async code => {
    try {
      const response = await CreditFlow.getDealerCenters(code)
      if (response.data) {
        setDealerCenters(response.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getDealerCenterName = async code => {
    try {
      const response = await CreditFlow.getDealerCenterName(code)
      if (response.data) {
        setDealerCenter(response.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getCarBrands = async () => {
    try {
      const response = await CreditFlow.getCarBrands()
      if (response.data) {
        setCarBrands(response.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getCarModels = async brand => {
    try {
      const response = await CreditFlow.getCarModels(brand)
      if (response.data) {
        setCarModels(response.data)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const getPrintForm = async data => {
    try {
      const response = await CreditFlow.getPrintForm(data)

      standardCheck(response, () => {
        if (response.data) {
          setPrintForms(response?.data?.files)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getAgreementFiles = async clientId => {
    try {
      const response = await CreditFlow.getAgreementFiles(clientId)
      standardCheck(response, () => {
        if (response.data) {
          enqueueSnackbar('Файл Согласие получен', { variant: 'info' })
          setAgreementFile(response?.data)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getSingleFile = async (clientId, loanApplicationId) => {
    try {
      const response = await CreditFlow.getSingleFile(clientId, loanApplicationId)
      standardCheck(response, () => {
        if (response.data) {
          enqueueSnackbar('Файл Анкеты получен', { variant: 'info' })
          setSingleFile(response?.data)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getGaranteeFile = async id => {
    try {
      const response = await CreditFlow.getGaranteeFile(id)
      standardCheck(response, () => {
        if (response.data) {
          enqueueSnackbar('Гарантийное письмо получено', { variant: 'info' })
          setGaranteeFile(response?.data)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const updateRepaymentDate = async (data, reset = () => {}) => {
    try {
      const response = await CreditFlow.updateRepaymentDate(data)
      standardCheck(response, () => {
        if (response.data) {
          enqueueSnackbar('Успешно', { variant: 'info' })
          reset()
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getCreditProducts = async (name = '') => {
    try {
      const data = {
        name,
        limit: 1000,
        offset: 0
      }
      const response = await CreditFlow.getCreditProducts(data)
      standardCheck(response, () => {
        if (response.data) {
          setCreditProducts(response.data.list)
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const assignByRole = async () => {
    try {
      authStoreFunc.setLoading(true)
      const data = {
        assignTo: authStore?.user?.data?.username,
        assignToFullName: getFullName(authStore?.user?.data?.firstName, authStore?.user?.data?.lastName),
        role: authStore.isUserRole
      }
      const response = await CreditFlow.assignByRole(data)
      standardCheck(response, () => {
        if (response.data) {
          setLoanApplicationData(response.data)
        }
      })
    } catch (error) {
      console.error(error)
    } finally {
      authStoreFunc.setLoading(false)
    }
  }

  const validateFields = (fieldDefinitions, formData) => {
    const errors = fieldDefinitions.reduce((acc, field) => {
      const { name, validate, error } = field
      if (typeof validate !== 'function') {
        return acc
      }
      const isValid = validate(formData[name])
      if (!isValid) {
        acc[name] = error
      }
      return acc
    }, {})

    setErrorFields(errors)

    return Object.keys(errors).length === 0
  }

  const setRedBlackCheckBox = async data => {
    try {
      const response = await CreditFlow.changeClientById(data)
      standardCheck(response, () => {
        if (!response.data) {
          enqueueSnackbar('Ошибка обновления', { variant: 'error' })
        }
      })
    } catch (error) {
      console.error(error)
    }
  }

  const getBsClientApplications = async iin => {
    try {
      const response = await CreditFlow.getBsClientApplications(iin)
      let result = null
      standardCheck(response, () => {
        if (response.data) {
          enqueueSnackbar('Заявки из BS получены', { variant: 'info' })
          result = response.data
        }
      })
      return result
    } catch (error) {
      console.error(error)
      return null
    }
  }

  const fetchPrintForm = () => {
    if (authStore.isUserRole === MANAGER && loanApplicationData?.status?.code === 'userUploadClientBankDocs') {
      const data = {
        loanApplicationId: loanApplicationData?.id,
        contract_number: loanApplicationData?.dkpNumber,
        contract_create_date: loanApplicationData?.dkpDate,
        city: loanApplicationData?.city,
        dealer_name: loanApplicationData?.dealerName,
        dealer_center_id: loanApplicationData?.dealerCenterId
      }
      getPrintForm(data)
    }
  }

  const resignDocuments = async loanApplicationId => {
    try {
      const response = await CreditFlow.resignDocument(loanApplicationId)
      let result = null
      standardCheck(
        response,
        () => {
          if (response.data) {
            enqueueSnackbar('Запрос на переподписание отправлен', { variant: 'success' })
            if (loanApplicationData?.id) getApplicationById(loanApplicationData.id)
            result = true
          }
        },
        {
          onErrCustom: err => {
            enqueueSnackbar(err?.msg ?? 'Ошибка при переподписании', { variant: 'error' })
          }
        }
      )
      return result
    } catch (error) {
      console.error(error)
      return null
    }
  }

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchPrintForm()
    }, 5000)
    return () => clearInterval(intervalId)
  }, [authStore.isUserRole, loanApplicationData?.status?.code])

  const contractStore = {
    step,
    stepperList,
    stepCode,
    loanApplicationData,
    creditProducts,
    client,
    collateral,
    errorFields,
    chatHistory,
    managerWorking,
    fileCategories,
    newClient,
    newCollaterals,
    newLoanApplication,
    banks,
    bsData,
    dealerCenters,
    dealerCenter,
    alternatives,
    printForms,
    carBrands,
    carModels,
    dds,
    clientFileTypes,
    collateralFileTypes,
    AgreementFile,
    SingleFile,
    GaranteeFile,
    ClientDocs
  }

  const contractStoreFunc = {
    getApplicationById,
    getClientById,
    getCollateralById,
    getChatHistory,
    getFileCategories,
    addComment,
    validateFields,
    getCreditProducts,
    setLoanApplicationData,
    setManagerWorking,
    setNewClient,
    setNewCollaterals,
    setNewLoanApplication,
    setErrorFields,
    getBanks,
    getBsData,
    getDealerCenters,
    getDealerCenterName,
    getAlternatives,
    getPrintForm,
    updateRepaymentDate,
    getCarBrands,
    getCarModels,
    assignByRole,
    getDDS,
    setFileCategories,
    getAgreementFiles,
    getSingleFile,
    getGaranteeFile,
    setClientDocs,
    setRedBlackCheckBox,
    getBsClientApplications,
    resignDocuments
  }

  return (
    <ContractStoreContext.Provider value={{ contractStore, contractStoreFunc }}>
      {props.children}
    </ContractStoreContext.Provider>
  )
}

export default ContractStoreProvider
